import React from 'react';
import { SentenceReportWrapper } from '@seedlang/hoc';
import { observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import styled from '@emotion/styled';
import { AppUI, DeckUI, OverlayUI } from '@seedlang/state';
import Text from 'components/text';
import { flexCenterColumn } from '@seedlang/style_mixins';
import { isPresent } from '@seedlang/utils';
import { LanguageIcon, FlagTranslationIcon } from '@seedlang/icons';
import SourceListWithInput from 'components/source/source_list_with_input';
import MeaningsList from 'components/vocab/meanings_list';
import DictionarySentences from 'components/vocab/dictionary_sentences';
import ReportMeaningsModal from 'components/vocab/report_meanings_modal';
import { noop, some } from 'lodash';
import RelatedWords from 'components/vocab/related_words';
import MeaningsToggleButton from 'components/vocab/meanings_toggle_button';
import { Languages } from '@seedlang/constants';

const TableWrapper = styled.div`
  margin: ${props => props.margin || '10px 0 0 0'};
`;

const Table = styled.div`
  padding: ${props => props.padding};
  ${flexCenterColumn()}
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: ${props => props.padding || '0 10px'};
  margin: ${props => props.margin};
  justify-content: ${props => props.justifyContent};
  box-sizing: ${props => props.isMobile ? 'border-box' : null};
`;

const TranslationWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;

const LanguageIconWrapper = styled.div`
  margin: ${props => props.margin};
  width: ${props => props.width};
`;

const LinkedWord = styled.span`
  border-bottom: ${props => props.noBorder ? '' : '2px solid #b5b5b5'};
  cursor: pointer;
`;


const ReportIssueButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #888;
  font: inherit;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover, &:focus-visible { background: #F1F1F1; }
  &:active { background: #E5E5E5; }
`;


@observer
class VocabWordInfoList extends React.Component {
  @computed get sentencesReadyToReport() {
    return this.props.sentencesReadyToReport(this.displayMeaningPreviews, this.displayMeaningsSection);
  }

  @observable showTriviaQuestionModal = false;
  @observable guestShowingMeaningsView = true;
  @observable showReportMeaningsModal = false;

  componentDidMount() {
    document.getElementById('top').scrollIntoView();
  }

  @computed get fontSize() {
    return AppUI.layout.isMobile ? '13px' : '16px';
  }

  @computed get lineHeight() {
    return AppUI.layout.isMobile ? '17px' : '22px';
  }

  @computed get targetText() {
    if (AppUI.site.hideDefiniteArticle) {
      return this.props.word.targetTextWithGender;
    } else {
      return this.props.word.targetTextWithDefiniteArticle;
    }
  }

  @computed get pluralTargetText() {
    if (AppUI.site.hideDefiniteArticle) {
      return this.props.word.pluralNounsStringWithoutDefiniteArticle;
    } else {
      return this.props.word.pluralNounsString;
    }
  }

  @computed get genderVariantTargetText() {
    if (AppUI.site.hideDefiniteArticle) {
      return this.props.word.genderVariant.targetTextWithGender;
    } else {
      return this.props.word.genderVariant.targetTextWithDefiniteArticle;
    }
  }

  @computed get languageName() {
    if (!this.props.word.languageId) return undefined;
    return Languages[this.props.word.languageId];
  }

  @computed get wordHasMeanings() {
    return this.props.word?.meanings && this.props.word.meanings.length > 0;
  }

  @computed get wordHasMeaningPreviews() {
    return this.props.word?.meaningSourcePreviews?.length > 0;
  }

  @computed get wordHasMeaningsData() {
    return this.wordHasMeanings || this.wordHasMeaningPreviews || this.props.word?.meaningsCount > 0;
  }

  @computed get isShowingMeaningsView() {
    if (this.props.summaryOnly) return true;
    if (isPresent(AppUI.user?.id)) {
      return AppUI.user.showVocabMeanings !== false;
    }
    return this.guestShowingMeaningsView;
  }

  handleMeaningsViewToggle = () => {
    const next = !this.isShowingMeaningsView;
    if (isPresent(AppUI.user?.id)) {
      AppUI.user.showVocabMeanings = next;
      AppUI.authUserStore.update({ data: { showVocabMeanings: next } }, noop);
      return;
    }
    this.guestShowingMeaningsView = next;
  };

  @computed get userCanAccessMeanings() {
    return AppUI.user?.showMeanings;
  }

  @computed get displayMeaningsList() {
    return this.userCanAccessMeanings &&
      this.wordHasMeanings &&
      this.isShowingMeaningsView;
  }

  @computed get displayMeaningPreviews() {
    return this.userCanAccessMeanings &&
      this.isShowingMeaningsView &&
      !this.wordHasMeanings &&
      this.wordHasMeaningPreviews;
  }

  @computed get displayMeaningsSection() {
    return this.displayMeaningsList || this.displayMeaningPreviews;
  }

  openReportMeanings = () => {
    if (this.props.inDeck || DeckUI.hasDeck) {
      OverlayUI.addOverlay('reportMeanings', {
        open: true,
        word: this.props.word,
        visibleSentencesByMeaning: this.props.dictionaryMeanings ? this.props.visibleSentencesByMeaning : undefined,
        onClose: () => {
          OverlayUI.removeOverlay('reportMeanings');
        },
        onSubmitSuccess: () => {
          DeckUI.createEvent('vocab - report meanings submitted', {
            word_id: this.props.word.id,
          });
        },
      });
      return;
    }
    this.showReportMeaningsModal = true;
  }

  render() {
    return (
      <TableWrapper
        isMobile={AppUI.layout.isMobile}
          margin={this.props.margin}
      >
        <Table
          padding={this.props.tablePadding || '10px 0 0 0'}
        >
          {!this.props.hideHeader && <>
          <Row
            isMobile={AppUI.layout.isMobile}
            margin='5px 0 5px 0'
            padding='0 0 0 10px'
          >
            <LanguageIconWrapper
              width='25px'
              aria-label={this.languageName}
            >
              <LanguageIcon
                languageId={this.props.word.languageId}
              />
            </LanguageIconWrapper>
            <TranslationWrapper>
              <Text
                bold
                fontSize='22px'
                lineHeight='26px'
                style={{ whiteSpace: 'nowrap' }}
                lang={this.props.word.languageId}
              >
                {this.targetText}{this.props.word.pluralRoot ? ' (pl.)' : ''}
              </Text>
              {
                isPresent(this.props.word.genderVariant) && this.genderVariantTargetText !== this.props.word.targetText &&
                <Text
                  fontSize='22px'
                  lineHeight='26px'
                  style={{ whiteSpace: 'nowrap' }}
                >
                  ,&nbsp;
                </Text>
              }
              {
                isPresent(this.props.word.genderVariant) && this.genderVariantTargetText !== this.props.word.targetText &&
                <Text
                  fontSize='22px'
                  lineHeight='26px'
                  style={{ whiteSpace: 'nowrap' }}
                  className={this.props.word.genderVariant.root ? 'underline' : ''}
                  onClick={this.props.word.genderVariant.root && this.props.onClickWord ? () => this.props.onClickWord(this.props.word.genderVariant) : noop}
                  lang={this.props.word.languageId}
                >
                  <LinkedWord>
                    {this.genderVariantTargetText}{this.props.word.genderVariant.pluralRoot ? ' (pl.)' : ''}
                  </LinkedWord>
                </Text>
              }
            </TranslationWrapper>
            {
              !this.props.dictionaryMeanings && this.displayMeaningsSection &&
                <FlagTranslationIcon
                  minimumHeight='20px'
                  message='Report a problem with these meanings'
                  onClick={this.openReportMeanings}
                />
            }
            {
              !this.props.dictionaryMeanings && this.userCanAccessMeanings && this.wordHasMeaningsData && !AppUI.layout.isMobile &&
                <MeaningsToggleButton
                  wordHasMeanings={this.wordHasMeaningsData}
                  isShowingMeaningsView={this.isShowingMeaningsView}
                  onToggle={this.handleMeaningsViewToggle}
                />
            }
          </Row>
          {
            this.props.showPlural && this.props.word.isNoun && !this.props.word.pluralRoot &&
            <Row
              isMobile={AppUI.layout.isMobile}
              margin='5px 0 5px 0'
            >
              <LanguageIconWrapper
                width='25px'
                aria-label={this.languageName}
              >
                <LanguageIcon
                  languageId={this.props.word.languageId}
                />
              </LanguageIconWrapper>
              <TranslationWrapper>
                <Text
                  center
                  fontSize={this.fontSize}
                  lineHeight={this.lineHeight}
                  lang={this.props.word.languageId}
                >
                  {this.pluralTargetText}{' (pl.)'}
                </Text>
              </TranslationWrapper>
            </Row>
          }
          {
            this.props.conjugationPreview &&
            <Row isMobile={AppUI.layout.isMobile} margin="5px 0 5px 0">
              <TranslationWrapper>
                <Text italic fontSize={this.fontSize} lineHeight={this.lineHeight} lang={this.props.word.languageId}>
                  {this.props.conjugationPreview}
                </Text>
              </TranslationWrapper>
            </Row>
          }
          {
            this.props.showNoPlural &&
            <Row
              isMobile={AppUI.layout.isMobile}
              margin='5px 0 5px 0'
            >
              <TranslationWrapper>
                <Text
                  center
                  italic
                  margin='0 0 0 25px'
                  fontSize={this.fontSize}
                  lineHeight={this.lineHeight}
                >
                  No Plural
                </Text>
              </TranslationWrapper>
            </Row>
          }
          </>}
          {!this.props.headerOnly && <>
          {
            this.displayMeaningsSection ? (
              <>
                <MeaningsList
                  onSentencePage={this.props.onSentencePage}
                  onSentencesLoaded={this.props.onSentencesLoaded}
                  word={this.props.word}
                  onPlaySentence={this.props.onPlaySentence}
                  dictionarySentences={this.props.dictionaryMeanings}
                  summaryOnly={this.props.summaryOnly}
                  background='#FFF'
                  fontSize={this.fontSize}
                  lineHeight={this.lineHeight}
                  padding='0 5px 5px 5px'
                  onClickWord={this.props.onClickWord}
                  rowMargin={this.props.rowMargin}
                  usePreviewMeanings={this.displayMeaningPreviews}
                  selectedSynonymAssociation={this.props.selectedSynonymAssociation}
                />
                {this.props.dictionaryMeanings && !this.props.summaryOnly && this.sentencesReadyToReport && (
                  <Row
                    isMobile={AppUI.layout.isMobile}
                    padding='10px'
                    justifyContent='flex-end'
                  >
                    <ReportIssueButton type='button' onClick={this.openReportMeanings}>
                      <i className='fa fa-flag' aria-hidden='true' />
                      Report Issue
                    </ReportIssueButton>
                  </Row>
                )}
              </>
            ) : (
              <SourceListWithInput
                word={this.props.word}
                background='#FFF'
                fontSize={this.fontSize}
                lineHeight={this.lineHeight}
                showInput={this.props.showSourceInput}
                afterSubmitSourceInput={this.props.afterSubmitSourceInput}
                padding='0 5px 5px 5px'
                user={AppUI.user}
                afterFlag={this.props.afterFlag}
              />
            )
          }
          {this.props.dictionaryMeanings && !this.props.summaryOnly && !this.displayMeaningsSection && this.props.word?.id && (
            <div style={{ width: '100%' }}>
              <DictionarySentences
                onSentencePage={this.props.onSentencePage}
                onSentencesLoaded={this.props.onSentencesLoaded}
                key={this.props.word.id}
                wordId={this.props.word.id}
                allSentences
                hideHeader
                fontSize={this.fontSize}
                onPlaySentence={this.props.onPlaySentence}
              />
            </div>
          )}
          {
            !this.props.dictionaryMeanings && this.userCanAccessMeanings && this.wordHasMeaningsData && AppUI.layout.isMobile &&
              <Row
                isMobile={AppUI.layout.isMobile}
                padding='10px 0'
                justifyContent='flex-end'
              >
                <MeaningsToggleButton
                  wordHasMeanings={this.wordHasMeaningsData}
                  isShowingMeaningsView={this.isShowingMeaningsView}
                  onToggle={this.handleMeaningsViewToggle}
                />
                {
                  this.displayMeaningsSection &&
                    <FlagTranslationIcon
                      minimumHeight='20px'
                      message='Report a problem with these meanings'
                      onClick={this.openReportMeanings}
                    />
                }
              </Row>
          }
          </>}
        </Table>
        {!this.props.headerOnly && <>
        {
          !this.props.hideSynonyms && !this.displayMeaningsSection && isPresent(this.props.word.synonymAssociations) && some(this.props.word.synonymAssociations.map(item => item.synonym?.publicInVocab)) &&
          <RelatedWords
            margin={AppUI.layout.isMobile ? '10px' : '10px 0'}
            title='Synonyms'
            referenceWord={this.props.word}
            words={this.props.word.publicSynonyms}
            onClickWord={this.props.onClickWord}
          />
        }
        {
          !this.props.hideRelatedWords && isPresent(this.props.relatedWords) &&
          <RelatedWords
            margin={AppUI.layout.isMobile ? '10px' : '10px 0'}
            title='Related Words'
            referenceWord={this.props.word}
            words={this.props.relatedWords}
            onClickWord={this.props.onClickWord}
          />
        }
        </>}
        <ReportMeaningsModal
          visibleSentencesByMeaning={this.props.dictionaryMeanings ? this.props.visibleSentencesByMeaning : undefined}
          word={this.props.word}
          open={this.showReportMeaningsModal}
          onClose={() => {
            this.showReportMeaningsModal = false;
          }}
          onSubmitSuccess={() => {
            DeckUI.createEvent('vocab - report meanings submitted', {
              word_id: this.props.word.id,
            });
          }}
        />
      </TableWrapper>
    );
  }
}

export default SentenceReportWrapper(VocabWordInfoList);
